// React
import React, {
  Children,
  forwardRef,
  useState,
  useCallback,
  cloneElement,
  isValidElement,
} from "react";
import PropTypes from "prop-types";
// Helpers
import { isNumber } from "@mefisto/utils";
// Framework
import { classnames } from "ui/classnames";
import {
  useMediaQuery,
  makeStyles,
  alpha,
  Box,
  Fade,
  Card,
  Spinner,
} from "ui/components";
import { useTheme } from "theme";
import { Section } from "ui/section";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles(({ shadows, radius, palette }) => ({
  card: ({ shadow }) => ({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    width: "100%",
    height: "100%",
    boxShadow: shadows[shadow],
    borderRadius: radius.large,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
  }),
  transparent: {
    background: "transparent",
    border: "none",
    borderRadius: `${radius.none} !important`,
    boxShadow: "none",
  },
  backgroundImage: {
    backgroundPosition: "center",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
  },
  spinner: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
  overflow: {
    overflow: "visible",
  },
  default: ({ bgcolor }) => ({
    backgroundColor: bgcolor ?? palette.common.white,
    border: palette.border.surface.main,
  }),
  flat: ({ bgcolor }) => ({
    backgroundColor: bgcolor ?? palette.background.surface.main,
  }),
  glass: ({ bgcolor }) => ({
    backgroundColor: bgcolor ?? alpha(palette.common.white, 0.9),
    backdropFilter: "blur(8px)",
  }),
  border: ({ bgcolor, borderColor }) => ({
    backgroundColor: bgcolor ?? palette.background.surface.main,
    border: palette.border.surface.main,
    borderColor,
  }),
  selected: ({ bgcolor, borderColor }) => ({
    backgroundColor: bgcolor ?? alpha(palette.primary.main, 0.07),
    border: `2px solid ${borderColor ?? palette.primary.main}`,
  }),
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const Tile = forwardRef(
  (
    {
      context = "tile",
      value,
      display = "default",
      loading = false,
      fitContent,
      overflow,
      maxWidth,
      maxHeight,
      minWidth = "auto",
      minHeight,
      shadow = 0,
      bgcolor,
      borderColor,
      backgroundImage,
      className,
      spinnerComponent,
      spinnerProps,
      children,
    },
    ref
  ) => {
    // Styles
    const classes = useStyles({ shadow, bgcolor, borderColor });
    const { breakpoints } = useTheme();
    const fullScreen = useMediaQuery(breakpoints.up("sm"));
    // State
    const [hover, setHover] = useState("false");
    // Handlers
    const handleMouseOver = useCallback(() => {
      setHover("true");
    }, []);
    const handleMouseLeave = useCallback(() => {
      setHover("false");
    }, []);
    // Render
    return (
      <Section context={context} value={value}>
        <Box
          ref={ref}
          display="flex"
          margin="auto"
          width="100%"
          minWidth={fullScreen ? minWidth : "auto"}
          maxWidth={maxWidth ? maxWidth : "auto"}
          height={fitContent ? "fit-content" : "100%"}
          minHeight={
            minHeight ? (isNumber(minHeight) ? minHeight : 350) : "auto"
          }
          maxHeight={maxHeight}
          className={classnames(className)}
        >
          <Card
            className={classnames(classes.card, {
              [classes.default]: display === "default",
              [classes.transparent]: display === "transparent",
              [classes.flat]: display === "flat",
              [classes.border]: display === "border",
              [classes.glass]: display === "glass",
              [classes.selected]: display === "selected",
              [classes.backgroundImage]: !!backgroundImage,
              [classes.overflow]: overflow,
            })}
            style={{
              ...(backgroundImage && {
                backgroundImage: `url(${backgroundImage})`,
              }),
            }}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
          >
            <Fade in={!loading} timeout={0}>
              <Box height="100%">
                <Box display="flex" flexDirection="column" height="100%">
                  {Children.map(children, (child) =>
                    isValidElement(child)
                      ? cloneElement(child, { hover })
                      : null
                  )}
                </Box>
              </Box>
            </Fade>
            {loading && (
              <div className={classes.spinner}>
                {spinnerComponent ?? (
                  <Spinner size="small" position="center" {...spinnerProps} />
                )}
              </div>
            )}
          </Card>
        </Box>
      </Section>
    );
  }
);

Tile.propTypes = {
  context: PropTypes.string,
  value: PropTypes.string,
  display: PropTypes.oneOf([
    "default",
    "border",
    "flat",
    "transparent",
    "glass",
    "selected",
  ]),
  loading: PropTypes.bool,
  fitContent: PropTypes.bool,
  overflow: PropTypes.bool,
  minWidth: PropTypes.number,
  minHeight: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  shadow: PropTypes.number,
  bgcolor: PropTypes.string,
  backgroundImage: PropTypes.string,
  spinnerProps: PropTypes.object,
  spinnerComponent: PropTypes.node,
};

export default Tile;
